"use client";

import { Badge } from "@ocr-monorepo/ui/components/badge";
import { Button } from "@ocr-monorepo/ui/components/button";
import { Input } from "@ocr-monorepo/ui/components/input";
import { Label } from "@ocr-monorepo/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ocr-monorepo/ui/components/select";
import { Textarea } from "@ocr-monorepo/ui/components/textarea";
import { Custom } from "fumadocs-openapi/playground/client";
import {
  type CreateOpenAPIPageOptions,
} from "fumadocs-openapi/ui";
import { useEffect, useMemo, useState } from "react";
import { codeUsages } from "@/lib/openapi/code-usages";

function toTextValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

type APIExampleSelectorProps = Parameters<
  NonNullable<
    NonNullable<CreateOpenAPIPageOptions["operation"]>["APIExampleSelector"]
  >
>[0];
type PlaygroundParameterFieldName = Parameters<
  NonNullable<
    NonNullable<CreateOpenAPIPageOptions["playground"]>["renderParameterField"]
  >
>[0];
type PlaygroundParameterFieldData = Parameters<
  NonNullable<
    NonNullable<CreateOpenAPIPageOptions["playground"]>["renderParameterField"]
  >
>[1];
type PlaygroundBodyFieldName = Parameters<
  NonNullable<
    NonNullable<CreateOpenAPIPageOptions["playground"]>["renderBodyField"]
  >
>[0];
type PlaygroundBodyFieldData = Parameters<
  NonNullable<
    NonNullable<CreateOpenAPIPageOptions["playground"]>["renderBodyField"]
  >
>[1];

function APIExampleSelector({
  items,
  value,
  onValueChange,
}: APIExampleSelectorProps) {
  const selectedValue = value ?? items[0]?.id ?? "";

  return (
    <div className="w-full max-w-xs">
      <Select value={selectedValue} onValueChange={onValueChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select request example" />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PlaygroundParameterField({
  fieldName,
  param,
}: {
  fieldName: PlaygroundParameterFieldName;
  param: PlaygroundParameterFieldData;
}) {
  const { value, setValue } = Custom.useController(fieldName);
  const currentValue = toTextValue(value);
  const description = param.description;
  const fieldId = String(fieldName);

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Label htmlFor={fieldId}>{param.name}</Label>
        <Badge variant={param.required ? "default" : "secondary"}>
          {param.in}
        </Badge>
      </div>
      {description ? (
        <p className="text-xs text-muted-foreground">{description}</p>
      ) : null}
      <Input
        id={fieldId}
        value={currentValue}
        placeholder={param.required ? "Required value" : "Optional value"}
        onChange={(event) => {
          setValue(event.target.value);
        }}
      />
    </div>
  );
}

function PlaygroundBodyField({
  fieldName,
  info,
}: {
  fieldName: PlaygroundBodyFieldName;
  info: PlaygroundBodyFieldData;
}) {
  const { value, setValue } = Custom.useController([fieldName]);
  const serializedValue = useMemo(() => toTextValue(value), [value]);
  const [draft, setDraft] = useState(serializedValue);
  const fieldId = String(fieldName);

  useEffect(() => {
    setDraft(serializedValue);
  }, [serializedValue]);

  function applyDraft(nextValue: string) {
    const trimmed = nextValue.trim();
    if (trimmed.length === 0) {
      setValue("");
      return;
    }

    try {
      setValue(JSON.parse(nextValue));
    } catch {
      setValue(nextValue);
    }
  }

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Label htmlFor={fieldId}>Body</Label>
        <Badge variant="secondary">{info.mediaType}</Badge>
      </div>
      <Textarea
        id={fieldId}
        rows={8}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          applyDraft(draft);
        }}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            applyDraft(draft);
          }}
        >
          Apply Body
        </Button>
      </div>
    </div>
  );
}

const clientConfig = {
  codeUsages,
  operation: {
    APIExampleSelector,
  },
  playground: {
    renderParameterField(fieldName, param) {
      return <PlaygroundParameterField fieldName={fieldName} param={param} />;
    },
    renderBodyField(fieldName, info) {
      return <PlaygroundBodyField fieldName={fieldName} info={info} />;
    },
  },
} satisfies Pick<CreateOpenAPIPageOptions, "codeUsages" | "operation" | "playground">;

export default clientConfig;
